import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { SignOut, Trash, PencilSimple, Plus, Eye } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
  unread: 'bg-blue-100 text-blue-800',
  read: 'bg-gray-100 text-gray-800',
};

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '', slug: '', excerpt: '', content: '', cover_image: '', category: '', tags: [], published: false,
  });

  const axiosAuth = useCallback(() => {
    return axios.create({ baseURL: API, withCredentials: true });
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [statsRes, leadsRes, contactsRes, postsRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/leads'),
        api.get('/api/contacts'),
        api.get('/api/blog?published_only=false'),
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setContacts(contactsRes.data);
      setPosts(postsRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [axiosAuth]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
    if (user) fetchAll();
  }, [user, authLoading, navigate, fetchAll]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await axiosAuth().patch(`/api/leads/${id}/status`, { status });
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axiosAuth().delete(`/api/leads/${id}`);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const openBlogDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setBlogForm({
        title: post.title, slug: post.slug, excerpt: post.excerpt,
        content: post.content, cover_image: post.cover_image || '',
        category: post.category || '', tags: post.tags || [], published: post.published,
      });
    } else {
      setEditingPost(null);
      setBlogForm({ title: '', slug: '', excerpt: '', content: '', cover_image: '', category: '', tags: [], published: false });
    }
    setBlogDialogOpen(true);
  };

  const saveBlogPost = async () => {
    try {
      if (editingPost) {
        await axiosAuth().put(`/api/blog/${editingPost.id}`, blogForm);
      } else {
        await axiosAuth().post('/api/blog', blogForm);
      }
      setBlogDialogOpen(false);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const deleteBlogPost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axiosAuth().delete(`/api/blog/${id}`);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const togglePublish = async (post) => {
    try {
      await axiosAuth().put(`/api/blog/${post.id}`, { published: !post.published });
      fetchAll();
    } catch (err) { console.error(err); }
  };

  if (authLoading) return <div className="flex items-center justify-center min-h-[80vh]"><p className="text-sm text-gray-500">Loading...</p></div>;
  if (!user) return null;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9FAFB]">
      {/* Admin Header */}
      <div className="bg-white border-b border-black/10 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black tracking-tighter text-black">Dashboard</h1>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <Button data-testid="admin-logout" onClick={handleLogout} variant="outline" className="border-black/20 rounded-[4px] text-sm gap-2">
            <SignOut size={16} /> Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="admin-tabs" className="bg-white border border-black/10 rounded-[4px] mb-8">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-[4px] text-sm">Overview</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-[4px] text-sm">Leads</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-[4px] text-sm">Contacts</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="rounded-[4px] text-sm">Blog</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-black/10 bg-white">
              {[
                { label: 'Total Leads', value: stats.total_leads || 0 },
                { label: 'New Leads', value: stats.new_leads || 0 },
                { label: 'Contacts', value: stats.total_contacts || 0 },
                { label: 'Blog Posts', value: stats.total_posts || 0 },
                { label: 'Published', value: stats.published_posts || 0 },
              ].map((s, i) => (
                <div key={i} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`} className={`p-6 ${i < 4 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{s.label}</p>
                  <p className="text-3xl font-black text-black">{s.value}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Leads */}
          <TabsContent value="leads">
            <div className="bg-white border border-black/10">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-bold uppercase">Name</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Email</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Company</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Revenue</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-sm text-gray-500 py-8">No leads yet</TableCell></TableRow>
                  ) : leads.map((lead) => (
                    <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                      <TableCell className="text-sm font-medium">{lead.name}</TableCell>
                      <TableCell className="text-sm">{lead.email}</TableCell>
                      <TableCell className="text-sm">{lead.company}</TableCell>
                      <TableCell className="text-sm">{lead.monthly_revenue}</TableCell>
                      <TableCell>
                        <Select value={lead.status} onValueChange={(val) => updateLeadStatus(lead.id, val)}>
                          <SelectTrigger data-testid={`lead-status-${lead.id}`} className="w-[120px] h-8 text-xs rounded-[4px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="qualified">Qualified</SelectItem>
                            <SelectItem value="lost">Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : ''}</TableCell>
                      <TableCell>
                        <Button data-testid={`lead-delete-${lead.id}`} variant="ghost" size="sm" onClick={() => deleteLead(lead.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts">
            <div className="bg-white border border-black/10">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-bold uppercase">Name</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Email</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Phone</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Message</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-gray-500 py-8">No contacts yet</TableCell></TableRow>
                  ) : contacts.map((c) => (
                    <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                      <TableCell className="text-sm font-medium">{c.name}</TableCell>
                      <TableCell className="text-sm">{c.email}</TableCell>
                      <TableCell className="text-sm">{c.phone}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{c.message}</TableCell>
                      <TableCell className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Blog */}
          <TabsContent value="blog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-black">Blog Posts</h2>
              <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="blog-create-btn" onClick={() => openBlogDialog()} className="bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] text-sm gap-2">
                    <Plus size={16} /> New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="blog-dialog-desc">
                  <DialogHeader>
                    <DialogTitle>{editingPost ? 'Edit Post' : 'New Post'}</DialogTitle>
                    <p id="blog-dialog-desc" className="text-sm text-gray-500">Fill in the details below to {editingPost ? 'update' : 'create'} a blog post.</p>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm font-medium">Title</Label>
                      <Input data-testid="blog-form-title" value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} className="mt-1 rounded-[4px]" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Slug</Label>
                      <Input data-testid="blog-form-slug" value={blogForm.slug} onChange={e => setBlogForm({...blogForm, slug: e.target.value})} className="mt-1 rounded-[4px]" placeholder="url-friendly-slug" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Category</Label>
                      <Input data-testid="blog-form-category" value={blogForm.category} onChange={e => setBlogForm({...blogForm, category: e.target.value})} className="mt-1 rounded-[4px]" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Cover Image URL</Label>
                      <Input data-testid="blog-form-image" value={blogForm.cover_image} onChange={e => setBlogForm({...blogForm, cover_image: e.target.value})} className="mt-1 rounded-[4px]" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Excerpt</Label>
                      <Textarea data-testid="blog-form-excerpt" value={blogForm.excerpt} onChange={e => setBlogForm({...blogForm, excerpt: e.target.value})} rows={2} className="mt-1 rounded-[4px]" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Content (Markdown)</Label>
                      <Textarea data-testid="blog-form-content" value={blogForm.content} onChange={e => setBlogForm({...blogForm, content: e.target.value})} rows={10} className="mt-1 rounded-[4px]" />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="published"
                        data-testid="blog-form-published"
                        checked={blogForm.published}
                        onChange={e => setBlogForm({...blogForm, published: e.target.checked})}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="published" className="text-sm">Publish immediately</Label>
                    </div>
                    <Button data-testid="blog-form-save" onClick={saveBlogPost} className="w-full bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] text-sm font-semibold">
                      {editingPost ? 'Update Post' : 'Create Post'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white border border-black/10">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-bold uppercase">Title</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Category</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-gray-500 py-8">No blog posts yet</TableCell></TableRow>
                  ) : posts.map((post) => (
                    <TableRow key={post.id} data-testid={`blog-row-${post.id}`}>
                      <TableCell className="text-sm font-medium">{post.title}</TableCell>
                      <TableCell className="text-sm">{post.category}</TableCell>
                      <TableCell>
                        <Badge
                          data-testid={`blog-status-${post.id}`}
                          className={`text-xs cursor-pointer ${post.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                          onClick={() => togglePublish(post)}
                        >
                          {post.published ? 'Published' : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}</TableCell>
                      <TableCell className="flex gap-1">
                        <Button data-testid={`blog-edit-${post.id}`} variant="ghost" size="sm" onClick={() => openBlogDialog(post)}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button data-testid={`blog-delete-${post.id}`} variant="ghost" size="sm" onClick={() => deleteBlogPost(post.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
